import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Banknote, Armchair, BellRing, Check, Plus, X, Hand, Users,
} from "lucide-react";
import Logo from "./Logo";
import { money, fmtDateTime, post, TABLE_STATUS, METHOD_LABELS } from "../api";
import { RollingNumber } from "../motion";

export default function MozoView({ state, setState, isMobile, mozoId, setMozoId }) {
  const [openTable, setOpenTable] = useState(false);
  const [selTable, setSelTable] = useState(null);
  const [people, setPeople] = useState(2);
  const [pickDish, setPickDish] = useState({});
  const [tab, setTab] = useState("mesas"); // mesas | propinas | reseñas

  const mozos = state?.mozos || [];
  const dishes = state?.dishes || [];
  const orders = state?.orders || [];
  const reviews = state?.reviews || [];
  const mozo = mozos.find((m) => m.id === mozoId) || mozos[0];
  const myTables = (state?.tables || []).filter((t) => t.mozo_id === mozo?.id);
  const myAlerts = (state?.alerts || []).filter((a) => a.status === "pendiente" && myTables.some((t) => t.number === a.table));
  const myTips = state?.waiter_tips?.[mozo?.id] || { tips: 0, tables_served: 0, stars: 0 };
  const myReviews = reviews.filter((r) => r.mozo_id === mozo?.id).slice(0, 6);

  const tableTotal = (n) => orders.filter((o) => o.table === n && !o.paid)
    .reduce((s, o) => s + o.items.reduce((a, i) => a + i.price * i.qty, 0), 0);

  const resolveAlert = async (id) => {
    const res = await post(`/alerts/${id}/resolve`, {});
    setState(res.state);
  };

  const openMesa = async () => {
    if (!selTable) return;
    const items = Object.entries(pickDish).filter(([, qty]) => qty > 0).map(([dish_id, qty]) => ({ dish_id, qty, note: "" }));
    const res = await post("/tables/open", { table: selTable, people, mozo_id: mozo.id, items });
    setState(res.state);
    setOpenTable(false);
    setSelTable(null);
    setPickDish({});
  };

  const libres = (state?.tables || []).filter((t) => t.status === "libre");

  return (
    <div className={`h-full flex flex-col bg-[#FAFAF7] ${!isMobile ? "max-w-[900px] mx-auto w-full" : ""}`} data-testid="mozo-view">
      {/* header mozo */}
      <div className="shrink-0 bg-[#0A0A0A] text-white px-4 py-3 flex items-center gap-3">
        <Logo size={30} />
        <div className="flex-1 min-w-0">
          <h1 className="font-extrabold lowercase text-sm leading-none">vista mozo</h1>
          <div className="flex gap-1.5 mt-1.5">
            {mozos.map((m) => (
              <button key={m.id} data-testid={`mozo-pick-${m.id}`} onClick={() => setMozoId(m.id)}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-lg transition ${mozo?.id === m.id ? "bg-[#A0CDE8] text-[#0A0A0A]" : "bg-white/10 text-white/70"}`}>
                {m.name.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-white/50 lowercase">propinas hoy</p>
          <p className="font-mono2 font-bold text-[#A0CDE8]" data-testid="mozo-tips">{money(myTips.tips)}</p>
        </div>
      </div>

      {/* alertas de sus mesas */}
      {myAlerts.length > 0 && (
        <div className="shrink-0 px-4 pt-3 space-y-2" data-testid="mozo-alerts">
          {myAlerts.map((a) => (
            <div key={a.id} data-testid={`mozo-alert-${a.id}`}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-xs font-bold ${a.type === "efectivo" ? "bg-[#FEE2E2] text-[#DC2626] border-2 border-[#DC2626]" : "bg-[#FEF9C3] text-[#a16207] border border-[#EAB308]"}`}>
              {a.type === "efectivo" ? <Banknote size={14} className="shrink-0" /> : <Hand size={13} className="shrink-0" />}
              <span className="flex-1">{a.message}</span>
              <button data-testid={`mozo-resolve-${a.id}`} onClick={() => resolveAlert(a.id)}
                className="shrink-0 bg-[#0A0A0A] text-white px-2.5 py-1 rounded-md"><Check size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* tabs */}
      <div className="shrink-0 flex gap-2 px-4 pt-3 pb-2" data-testid="mozo-tabs">
        {[{ id: "mesas", l: "mis mesas" }, { id: "propinas", l: "propinas" }, { id: "resenas", l: "reseñas" }].map((t) => (
          <button key={t.id} data-testid={`mozo-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition ${tab === t.id ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#E5E7EB]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === "mesas" && (
          <div>
            <div className={`grid ${isMobile ? "grid-cols-2" : "grid-cols-3"} gap-3`}>
              {myTables.map((tb) => {
                const st = TABLE_STATUS[tb.status] || TABLE_STATUS.libre;
                return (
                  <div key={tb.number} data-testid={`mozo-table-${tb.number}`}
                    className="rounded-lg border-2 p-3.5" style={{ background: st.color, borderColor: st.border, color: st.text }}>
                    <p className="font-extrabold text-sm">{tb.name}</p>
                    <p className="text-[10px] font-bold">{st.label}</p>
                    {tb.status !== "libre" && <p className="font-mono2 text-xs font-bold mt-1">{money(tableTotal(tb.number))}</p>}
                    {tb.opened_by && <p className="text-[9px] mt-0.5 opacity-70">abierta por {tb.opened_by === "mozo" ? "mozo" : "QR"}</p>}
                  </div>
                );
              })}
            </div>
            <button data-testid="mozo-open-table" onClick={() => setOpenTable(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg hover:brightness-95 active:scale-[0.98] transition">
              <Plus size={16} /> abrir mesa
            </button>
          </div>
        )}

        {tab === "propinas" && (
          <div className="space-y-3">
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 text-center">
              <p className="text-xs font-bold text-neutral-500 lowercase">tus propinas del turno</p>
              <RollingNumber value={myTips.tips} duration={0.8} format={(v) => money(v)} className="font-mono2 font-extrabold text-4xl mt-2 block" />
              <div className="flex justify-center gap-6 mt-3 text-xs text-neutral-500">
                <span>mesas <b className="font-mono2 text-[#0A0A0A]">{myTips.tables_served}</b></span>
                <span>promedio <b className="font-mono2 text-[#0A0A0A]">{myTips.stars}★</b></span>
              </div>
            </div>
            <div className="bg-[#E3F0F9] border border-[#A0CDE8] rounded-lg p-4 text-xs font-semibold leading-relaxed">
              Las propinas se asignan según la configuración del dueño: a vos, al local o a un pozo común.
            </div>
          </div>
        )}

        {tab === "resenas" && (
          <div className="space-y-2.5">
            {myReviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`mozo-review-${r.id}`}>
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <span className={`font-mono2 font-bold ${r.stars >= 4 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{r.stars}★</span>
                  <span>Mesa {r.table}</span>
                  <span className="font-mono2 ml-auto">{fmtDateTime(r.at)}</span>
                </div>
                <p className="text-sm mt-1.5">{r.text}</p>
              </div>
            ))}
            {myReviews.length === 0 && <p className="text-center text-neutral-500 py-12 text-sm">Todavía no tenés reseñas este turno.</p>}
          </div>
        )}
      </div>

      {/* modal abrir mesa */}
      <AnimatePresence>
        {openTable && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="open-table-modal">
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-t-2xl sm:rounded-lg p-5 w-full sm:w-[440px] max-h-[86vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <p className="font-extrabold lowercase">abrir mesa</p>
                <button data-testid="open-table-close" onClick={() => setOpenTable(false)}><X size={18} /></button>
              </div>
              <p className="text-xs font-bold text-neutral-500 mb-2 lowercase">elegí la mesa</p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {libres.map((tb) => (
                  <button key={tb.number} data-testid={`pick-table-${tb.number}`} onClick={() => setSelTable(tb.number)}
                    className={`rounded-lg border-2 py-2.5 text-sm font-bold transition ${selTable === tb.number ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB]"}`}>
                    {tb.number}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Users size={15} className="text-neutral-400" />
                <button onClick={() => setPeople(Math.max(1, people - 1))} className="w-8 h-8 rounded-lg border border-[#E5E7EB] font-bold">−</button>
                <span className="font-mono2 font-bold" data-testid="open-people">{people}</span>
                <button onClick={() => setPeople(Math.min(12, people + 1))} className="w-8 h-8 rounded-lg border border-[#E5E7EB] font-bold">+</button>
                <span className="text-xs text-neutral-500">personas</span>
              </div>
              <p className="text-xs font-bold text-neutral-500 mb-2 lowercase">pedido inicial (opcional)</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto mb-4">
                {dishes.filter((d) => d.available).map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-sm border border-[#E5E7EB] rounded-lg px-2.5 py-2">
                    <span className="font-semibold text-xs flex-1 truncate">{d.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setPickDish({ ...pickDish, [d.id]: Math.max(0, (pickDish[d.id] || 0) - 1) })} className="w-6 h-6 rounded border border-[#E5E7EB] text-xs font-bold">−</button>
                      <span className="font-mono2 text-xs w-4 text-center">{pickDish[d.id] || 0}</span>
                      <button data-testid={`open-add-${d.id}`} onClick={() => setPickDish({ ...pickDish, [d.id]: (pickDish[d.id] || 0) + 1 })} className="w-6 h-6 rounded border border-[#E5E7EB] text-xs font-bold">+</button>
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="open-table-confirm" onClick={openMesa} disabled={!selTable}
                className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg disabled:opacity-40 active:scale-[0.98] transition">
                abrir mesa {selTable ? `#${selTable}` : ""}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
